import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router';
import { styled } from '@mui/material/styles';
import { flatten, cloneDeep } from 'lodash';
import { NotificationGroupDto, ScoringInformationDto } from 'models/dtoTypes';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Grid from '@mui/material/Grid';
import SeenButton from 'components/SeenButton';
import { GetNotifications, MarkNotification, MarkAllNotification } from 'services/openapiClient';
import { getScoreColor } from 'utils/score';

const PREFIX = 'NotificationButton';

const classes = {
  notification: `${PREFIX}-notification`,
  tabHead: `${PREFIX}-tabHead`,
  tabHeadText: `${PREFIX}-tabHeadText`,
  markAll: `${PREFIX}-markAll`,
  menus: `${PREFIX}-menus`,
  list: `${PREFIX}-list`,
  listItem: `${PREFIX}-listItem`,
  listItemAction: `${PREFIX}-listItemAction`,
  listItemText: `${PREFIX}-listItemText`,
  secondary: `${PREFIX}-secondary`,
  listSection: `${PREFIX}-listSection`,
  ul: `${PREFIX}-ul`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  width: 500,
  maxHeight: '90vh',

  [`& .${classes.notification}`]: {
    color: 'white',
    marginRight: 20,
  },

  [`& .${classes.tabHead}`]: {
    // minWidth: 100,
    padding: 0,
    marginRight: 15,
  },

  [`& .${classes.tabHeadText}`]: {
    width: '100%',
    textAlign: 'left',
    paddingRight: 15,
  },
  [`& .${classes.markAll}`]: {
    textAlign: 'right',
    '& button': {
      lineHeight: '40px',
    },
  },

  [`& .${classes.menus}`]: {},

  [`& .${classes.list}`]: {
    width: '100%',
    maxHeight: '90%',
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
    overflow: 'overlay',
  },

  [`& .${classes.listItem}`]: {
    width: '100%',
    paddingLeft: 0,
    paddingRight: 0,
  },

  [`& .${classes.listItemAction}`]: {
    position: 'absolute',
    top: 5,
    right: 2,
    width: 30,
    height: 30,
  },

  [`& .${classes.listItemText}`]: {
    borderRadius: 4,
    margin: 0,
    '& svg': {
      color: 'white',
    },
  },

  [`& .${classes.secondary}`]: {
    paddingLeft: 50,
    paddingRight: 15,
    textAlign: 'right',
    color: 'white',
    textTransform: 'uppercase',
    fontSize: 9,
    paddingBottom: 15,
  },

  [`& .${classes.listSection}`]: {
    backgroundColor: 'inherit',
  },

  [`& .${classes.ul}`]: {
    backgroundColor: 'inherit',
    padding: 0,
  },
}));

type Severity = 'success' | 'warning' | 'info' | 'error';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ScoringInformationWithState extends ScoringInformationDto {
  scoringState: string;
}

const getAllNotificationsWithState = (notificationGroups: NotificationGroupDto[]) => {
  const flattenedWithState: ScoringInformationWithState[] = flatten(
    notificationGroups.map((n) => {
      const scoringState = n.scoringState || '';
      const list = n.information || [];
      return list.map((scoreInformation: ScoringInformationDto) => ({
        ...scoreInformation,
        scoringState,
      })) as ScoringInformationWithState[];
    }),
  );
  return flattenedWithState;
};

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nav-tabpanel-${index}`}
      aria-labelledby={`nav-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={0}>{children}</Box>}
    </div>
  );
}

interface Props {
  variant: 'standard' | 'dot';
}

interface OnMarkNotification {
  (id: string, markIsRead: boolean): Promise<void>;
}

interface OnMarkAll {
  (): Promise<void>;
}

interface NotificationPanelProps {
  loading: boolean;
  notificationGroups: NotificationGroupDto[];
  onMarkNotification: OnMarkNotification;
  onMarkAll: OnMarkAll;
}

function NotificationPanel(props: NotificationPanelProps) {
  const history = useHistory();
  const { loading, notificationGroups, onMarkNotification, onMarkAll } = props;
  const firstAvailable = () => {
    const groups = notificationGroups.map((g) => g.information);
    if (groups[0] && groups[0].length > 0) {
      return 0;
    } else if (groups[1] && groups[1].length > 0) {
      return 1;
    } else {
      return 2;
    }
  };

  const [value, setValue] = useState(firstAvailable());
  const [unreadOnly, setUnreadOnly] = useState(false);

  const handleChangeTab = (event: React.ChangeEvent<unknown>, newValue: number) => {
    setValue(newValue);
  };

  const handleClick = (item: ScoringInformationDto) => {
    const { sectionAlias, questionAlias, actionable, resourceUrl } = item;
    if (!actionable) {
      return null;
    } else if (resourceUrl) {
      window.open(resourceUrl, '_blank');
    } else if (sectionAlias) {
      const question = questionAlias as string;
      const hash = question ? `#${question}` : '';
      history.push(`/questionnaire/${sectionAlias}${hash}`);
    }
  };

  const renderNotification = (notification: ScoringInformationDto, index: number) => {
    const mapper = {
      Info: 'info',
      Success: 'success',
      Warning: 'warning',
      Danger: 'error',
    };
    const { actionable, message, type, scoreAlias } = notification;
    const color = getScoreColor(scoreAlias);
    return (
      <ListItem key={index} className={classes.listItem}>
        <ListItemText
          style={{ backgroundColor: color }}
          classes={{
            root: classes.listItemText,
            secondary: classes.secondary,
          }}
          primary={
            <Alert
              style={{ backgroundColor: color }}
              className={classes.notification}
              severity={(type ? mapper[type] : 'info') as Severity}
            >
              {actionable ? (
                <Link
                  underline="hover"
                  component="a"
                  variant="body2"
                  color="inherit"
                  onClick={() => handleClick(notification)}
                >
                  {message}
                </Link>
              ) : (
                message
              )}
            </Alert>
          }
          secondary={notification.scoreName || ''}
        />
        {value === 2 && (
          <div className={classes.listItemAction}>
            <SeenButton scoringInformation={notification} onMark={onMarkNotification} />
          </div>
        )}
      </ListItem>
    );
  };

  const handleSwitch = () => {
    setUnreadOnly(!unreadOnly);
  };

  const renderExtraMenuForInformation = (hasUnread?: boolean) => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <FormControlLabel
            control={<Switch checked={unreadOnly} onChange={handleSwitch} name="checkedA" />}
            label="Only show unread"
          />
        </Grid>
        {hasUnread && (
          <Grid item xs={6} className={classes.markAll}>
            <Link component="button" variant="body2" color="inherit" onClick={onMarkAll}>
              Mark all as read
            </Link>
          </Grid>
        )}
      </Grid>
    );
  };

  const filterUnread = (information: { seen?: boolean }[]) => {
    return value === 2 && unreadOnly ? information.filter((i) => !i.seen) : information;
  };

  const renderTab = (group: NotificationGroupDto, index: number) => {
    const information = group.information || [];
    const filtered = filterUnread(information);
    const hasUnread = information.filter((i) => !i.seen).length > 0;
    return (
      <TabPanel value={value} index={index} key={index}>
        <div className={classes.menus}>
          {value === 2 && renderExtraMenuForInformation(hasUnread)}
        </div>
        {filtered.length === 0 ? null : (
          <List className={classes.list} dense>
            {filtered.map(renderNotification)}
          </List>
        )}
      </TabPanel>
    );
  };

  const renderTabHead = (notificationGroup: NotificationGroupDto, i: number) => {
    const { scoringState } = notificationGroup;
    const information = notificationGroup.information || [];
    // only the 3rd tab cares about seen
    const filtered = i === 2 ? information.filter((i) => !i.seen) : information;
    const count = filtered.length;
    return (
      <Tab
        key={i}
        label={
          <div className={classes.tabHeadText}>
            <Badge
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              color="secondary"
              badgeContent={count}
            >
              <Typography fontSize={14}>{scoringState || ''}</Typography>
            </Badge>
          </div>
        }
        classes={{
          root: classes.tabHead,
        }}
      />
    );
  };

  const renderTabs = () => {
    if (loading) {
      return <CircularProgress size={25} />;
    } else {
      return (
        <>
          <Tabs
            value={value}
            onChange={handleChangeTab}
            indicatorColor="primary"
            textColor="primary"
          >
            {notificationGroups.map(renderTabHead)}
          </Tabs>
          {notificationGroups.map(renderTab)}
        </>
      );
    }
  };

  return (
    <StyledBox p={2}>
      <Typography variant="h6" fontSize={16}>
        Notifications
      </Typography>
      {renderTabs()}
    </StyledBox>
  );
}

export default function NotificationButton(props: Props) {
  const { variant = 'standard' } = props;
  const [loading, setLoading] = useState(false);
  const [notificationGroups, setNotificationGroups] = useState<NotificationGroupDto[]>([]);
  const [open, setOpen] = useState(false);
  const mountedRef = useRef(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((prev) => !prev);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      const notificationSummary = await GetNotifications();
      if (!mountedRef.current) {
        setLoading(false);
        return;
      }
      setNotificationGroups(notificationSummary?.notificationGroups || []);
      setLoading(false);
    };
    fetchNotifications();
    // unmonunt
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const formattedNotifications = React.useMemo(() => {
    const flattenedWithState = getAllNotificationsWithState(notificationGroups);
    const reGrouped = [
      {
        scoringState: 'Blocking',
        information: flattenedWithState.filter((i) => i.blocking),
      },
      {
        scoringState: 'In Progress',
        information: flattenedWithState.filter(
          (i) => i.scoringState === 'inPorgress' || i.scoringState === 'error',
        ),
      },
      {
        scoringState: 'Information',
        information: flattenedWithState.filter(
          (i) => i.scoringState != 'inPorgress' && i.scoringState !== 'error' && !i.blocking,
        ),
      },
    ];
    return reGrouped;
  }, [notificationGroups]);

  const getBadge = () => {
    const blockingCount = formattedNotifications[0].information.length;
    const inPorgressCount = formattedNotifications[1].information.length;
    const unread = formattedNotifications[2].information.filter((i) => !i.seen).length;
    return blockingCount + inPorgressCount + unread;
  };

  const handleMarkNotification = async (id: string, markIsRead: boolean) => {
    const updated = await MarkNotification({ id, markIsRead });
    if (updated) {
      // update notificationGroups to all seen
      const newGroups = cloneDeep(notificationGroups);
      newGroups.map((g) => {
        if (!g.information) {
          return g;
        }
        const { scoringState, information } = g;
        return {
          scoringState,
          information: information.map((i) => {
            if (i.id === id) {
              i.seen = markIsRead;
            }
            return i;
          }),
        } as NotificationGroupDto;
      });
      setNotificationGroups(newGroups);
    }
  };

  const handleMarkAll = async () => {
    const updated = await MarkAllNotification();
    if (updated) {
      // update notificationGroups to all seen
      const newGroups = cloneDeep(notificationGroups);
      newGroups.map((g) => {
        if (!g.information) {
          return g;
        }
        const { scoringState, information } = g;
        return {
          scoringState,
          information: information.map((i) => {
            i.seen = true;
            return i;
          }),
        } as NotificationGroupDto;
      });
      setNotificationGroups(newGroups);
    }
  };

  return (
    <div>
      <Popover
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <NotificationPanel
          loading={loading}
          notificationGroups={formattedNotifications}
          onMarkNotification={handleMarkNotification}
          onMarkAll={handleMarkAll}
        />
      </Popover>
      <IconButton color="inherit" onClick={handleClick}>
        <Badge
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          variant={variant}
          badgeContent={getBadge()}
          color="secondary"
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>
    </div>
  );
}
